import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Paper,
  CircularProgress,
  Alert,
  IconButton,
  Tooltip,
  Skeleton,
  FormControlLabel,
  Switch,
  Button
} from '@mui/material';
import { DataGrid, GridColDef, GridPaginationModel, GridSortModel, GridFilterModel } from '@mui/x-data-grid';
import { Download as DownloadIcon, Map as MapIcon } from '@mui/icons-material';
import { fetchEngagementDataV2, EngagementContact, EngagementResponse, DateRange, fetchGeographicAnalyticsV2, GeographicLocation } from '../../services/analyticsService';
import apiService from '../../services/apiService';
import { format, parseISO } from 'date-fns';
import { useUser } from '../../contexts/UserContext';
import ContactDetailModal from '../ContactDetailModal';
import GeographicMapModal from './GeographicMapModal';
import DateRangePicker from './DateRangePicker';
import { obfuscateEmail } from '../../utils/roleUtils';

interface EngagementProps {
  dateRange: DateRange;
  onDateRangeChange: (dateRange: DateRange) => void;
  includeSpamChecker?: boolean;
  onSpamCheckerChange?: (includeSpam: boolean) => void;
}

const Engagement: React.FC<EngagementProps> = ({ dateRange, onDateRangeChange, includeSpamChecker = false, onSpamCheckerChange }) => {
  const { selectedClientId, isICRole } = useUser();
  const [metric, setMetric] = useState<'opens' | 'clicks'>('clicks');
  const [sortBy, setSortBy] = useState<'top' | 'recent'>('top');
  const [contacts, setContacts] = useState<EngagementContact[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [totalCount, setTotalCount] = useState(0);
  const [selectedContact, setSelectedContact] = useState<EngagementContact | null>(null);
  const [mapModalOpen, setMapModalOpen] = useState(false);
  const [mapData, setMapData] = useState<any[]>([]);
  const [mapLoading, setMapLoading] = useState(false);
  const [mapError, setMapError] = useState<string | null>(null);
  const [isExporting, setIsExporting] = useState(false);
  const [exportProgress, setExportProgress] = useState({ current: 0, total: 0 });
  
  // DataGrid state
  const [paginationModel, setPaginationModel] = useState<GridPaginationModel>({
    page: 0,
    pageSize: 25,
  });
  const [sortModel, setSortModel] = useState<GridSortModel>([{ field: 'clicks', sort: 'desc' }]);
  const [filterModel, setFilterModel] = useState<GridFilterModel>({ items: [] });

  // Helper function to convert filter model to API parameters
  const getFilterParams = () => {
    const filters: any = {};
    filterModel.items.forEach(item => {
      if (item.value) {
        switch (item.field) {
          case 'email_address':
            filters.email_address = item.value;
            break;
          case 'first_name':
            filters.first_name = item.value;
            break;
          case 'last_name':
            filters.last_name = item.value;
            break;
          case 'segment_name':
            filters.segment_name = item.value;
            break;
        }
      }
    });
    return filters;
  };

  // Debounced API call to prevent excessive requests
  const debouncedFetch = useCallback(
    (() => {
      let timeoutId: NodeJS.Timeout;
      return (params: any) => {
        clearTimeout(timeoutId);
        timeoutId = setTimeout(() => {
          fetchEngagementData(params);
        }, 300); // 300ms delay
      };
    })(),
    [includeSpamChecker, paginationModel, sortModel, filterModel]
  );

  useEffect(() => {
    if (!selectedClientId) return;
    
    const params = {
      dateRange,
      selectedClientId
    };
    
    debouncedFetch(params);
  }, [dateRange, selectedClientId, includeSpamChecker, paginationModel, sortModel, filterModel, debouncedFetch]);

  const fetchEngagementData = async (params: any) => {
    if (!params.selectedClientId) return;
    
    try {
      setLoading(true);
      
      const response = await fetchEngagementDataV2(
        params.selectedClientId,
        'clicks', // Always use clicks
        'top', // Always use top sorting
        !includeSpamChecker, // Invert logic: when "Filter out spam" is false, include spam (true)
        paginationModel.page + 1, // Convert 0-based to 1-based for API
        paginationModel.pageSize, // Pass the selected page size
        params.dateRange.startDate?.toISOString().split('T')[0],
        params.dateRange.endDate?.toISOString().split('T')[0],
        undefined, // emailAddress
        undefined, // firstName
        undefined, // lastName
        undefined, // segmentName
        sortModel.length > 0 ? sortModel[0].field : undefined,
        sortModel.length > 0 ? sortModel[0].sort : undefined,
        false // includeGeographic
      );
      
      console.log('Engagement V2 API response:', response);
      
      setContacts(response.contacts || []);
      setTotalCount(response.total_count || 0);
      setError(null);
    } catch (err: any) {
      console.error('Engagement API error:', err);
      setError(`Failed to load engagement data: ${err.response?.status || 'Unknown error'}`);
      setContacts([]);
      setTotalCount(0);
    } finally {
      setLoading(false);
    }
  };


  const handlePaginationModelChange = (newModel: GridPaginationModel) => {
    setPaginationModel(newModel);
  };

  const handleSortModelChange = (newModel: GridSortModel) => {
    setSortModel(newModel);
  };

  const handleFilterModelChange = (newModel: GridFilterModel) => {
    setFilterModel(newModel);
  };

  const handleViewContact = (contact: EngagementContact) => {
    setSelectedContact(contact);
  };

  const handleCloseContactModal = () => {
    setSelectedContact(null);
  };

  const handleOpenMapModal = useCallback(async () => {
    setMapModalOpen(true);
    await fetchGeographicData();
  }, [selectedClientId, dateRange, includeSpamChecker]);

  const fetchGeographicData = async () => {
    if (!selectedClientId) return;
    
    try {
      setMapLoading(true);
      setMapError(null);
      
      const response = await fetchGeographicAnalyticsV2(
        selectedClientId,
        dateRange.startDate?.toISOString().split('T')[0],
        dateRange.endDate?.toISOString().split('T')[0],
        !includeSpamChecker, // Invert logic: when "Filter out spam" is false, include spam (true)
        1, // page
        1000 // limit
      );
      
      const locations = response.locations || [];
      
      // Group locations by latitude/longitude coordinates
      const geographicData = new Map<string, any>();
      
      locations.forEach((location: GeographicLocation) => {
        if (location.latitude && location.longitude) {
          const key = `${location.latitude},${location.longitude}`;
          
          if (!geographicData.has(key)) {
            geographicData.set(key, {
              latitude: location.latitude,
              longitude: location.longitude,
              count: 0,
              contacts: []
            });
          }
          
          const locationData = geographicData.get(key);
          locationData.count += location.click_count || 1;
          locationData.contacts.push({
            id: location.id,
            first_name: location.city, // Use city as name since we don't have contact names
            last_name: location.state,
            email_address: `${location.city}, ${location.state}, ${location.country}`
          });
        }
      });
      
      setMapData(Array.from(geographicData.values()));
    } catch (err: any) {
      console.error('Geographic data fetch error:', err);
      setMapError(`Failed to load geographic data: ${err.response?.status || 'Unknown error'}`);
      setMapData([]);
    } finally {
      setMapLoading(false);
    }
  };

  const handleCloseMapModal = useCallback(() => {
    setMapModalOpen(false);
  }, []);

  const getMetricLabel = () => {
    return metric === 'opens' ? 'Opens' : 'Clicks';
  };

  const getSortLabel = () => {
    return sortBy === 'top' ? 'Most' : 'Most Recent';
  };

  // Export contacts to CSV
  const handleExport = useCallback(async () => {
    if (!selectedClientId || isExporting) return;

    setIsExporting(true);
    setExportProgress({ current: 0, total: 0 });
    try {
      // Fetch all contacts for export using pagination
      let allContacts: EngagementContact[] = [];
      let clientName = 'unknown';

      // Fetch client name
      try {
        const clientResponse = await apiService.get(`/api/clients/${selectedClientId}`);
        if (clientResponse.data?.agency_name) {
          clientName = clientResponse.data.agency_name.replace(/[^a-zA-Z0-9]/g, '_').toLowerCase();
        }
      } catch (error) {
        console.warn('Could not fetch client name, using client ID:', error);
        clientName = `client_${selectedClientId}`;
      }

      const initialResponse = await fetchEngagementDataV2(
        selectedClientId,
        metric,
        sortBy,
        !includeSpamChecker,
        1, // page
        500, // limit (max allowed by API)
        dateRange.startDate?.toISOString().split('T')[0],
        dateRange.endDate?.toISOString().split('T')[0]
      );
      
      if (initialResponse?.contacts) {
        allContacts = initialResponse.contacts;
        const totalCount = initialResponse.total_count || 0;
        setExportProgress({ current: allContacts.length, total: totalCount });
      }

      // Continue fetching remaining pages if needed
      const limit = 500;
      let page = 2;
      let hasMore = allContacts.length < (initialResponse?.total_count || 0);

      while (hasMore) {
        const response = await fetchEngagementDataV2(
          selectedClientId,
          metric,
          sortBy,
          !includeSpamChecker,
          page,
          limit,
          dateRange.startDate?.toISOString().split('T')[0],
          dateRange.endDate?.toISOString().split('T')[0]
        );

        if (response?.contacts) {
          allContacts = allContacts.concat(response.contacts);
          
          // Update progress
          setExportProgress({ current: allContacts.length, total: initialResponse?.total_count || 0 });
          
          // Check if we've fetched all records
          if (allContacts.length >= (initialResponse?.total_count || 0) || response.contacts.length < limit) {
            hasMore = false;
          } else {
            page++;
          }
        } else {
          hasMore = false;
        }
      }

      if (allContacts.length === 0) {
        alert('No contacts to export');
        return;
      }

      // Generate filename based on client name, metric type, and date
      const dateStr = format(new Date(), 'yyyy-MM-dd');
      const filename = `${clientName}_engagement_${metric}_contacts_${dateStr}.csv`;

      // Create CSV headers
      const headers = ['First Name', 'Last Name', 'Email Address', 'Total Sent', 'Total Opened', 'Total Clicked', 'Unique Opens', 'Unique Clicks', 'Last Activity', 'Segment Name'];

      // Convert contacts to CSV rows
      const csvRows = [
        headers.join(','),
        ...allContacts.map((contact) => {
          const activityDate = contact.last_activity;
          const row = [
            `"${contact.first_name || ''}"`,
            `"${contact.last_name || ''}"`,
            `"${isICRole() ? obfuscateEmail(contact.email_address) : (contact.email_address || '')}"`,
            `"${contact.total_sent || 0}"`,
            `"${contact.total_opened || 0}"`,
            `"${contact.total_clicked || 0}"`,
            `"${contact.unique_opens || 0}"`,
            `"${contact.unique_clicks || 0}"`,
            `"${activityDate ? format(parseISO(activityDate), 'yyyy-MM-dd') : 'N/A'}"`,
            `"${contact.segment_name || ''}"`
          ];

          return row.join(',');
        })
      ];

      // Create and download CSV file
      const csvContent = csvRows.join('\n');
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);
      link.setAttribute('href', url);
      link.setAttribute('download', filename);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

    } catch (error) {
      console.error('Export failed:', error);
      alert('Failed to export contacts. Please try again.');
    } finally {
      setIsExporting(false);
    }
  }, [selectedClientId, isExporting, sortBy, metric, dateRange, includeSpamChecker, getFilterParams]);

  // DataGrid columns
  const columns: GridColDef[] = [
    {
      field: 'first_name',
      headerName: 'First Name',
      width: 150,
      sortable: true,
      filterable: true,
    },
    {
      field: 'last_name',
      headerName: 'Last Name',
      width: 150,
      sortable: true,
      filterable: true,
    },
    {
      field: 'email_address',
      headerName: 'Email Address',
      width: 250,
      sortable: true,
      filterable: true,
      renderCell: (params) => (
        <Button
          size="small"
          variant="text"
          color="primary"
          onClick={() => handleViewContact(params.row)}
          sx={{
            textAlign: 'left',
            justifyContent: 'flex-start',
            textTransform: 'none',
            fontWeight: 'normal'
          }}
        >
          {isICRole() ? obfuscateEmail(params.value) : params.value}
        </Button>
      ),
    },
    {
      field: 'last_activity',
      headerName: 'Last Activity',
      width: 150,
      sortable: true,
      valueFormatter: (params) => {
        const value = params?.value || params;
        if (!value) return 'N/A';
        try {
          return format(parseISO(value), 'yyyy-MM-dd');
        } catch (error) {
          console.error('Date parsing error:', error, 'for value:', value);
          return 'Invalid Date';
        }
      },
    },
    {
      field: 'total_sent',
      headerName: 'Sends',
      width: 100,
      sortable: true,
      type: 'number' as const,
    },
    {
      field: 'total_opened',
      headerName: 'Opens',
      width: 100,
      sortable: true,
      type: 'number' as const,
    },
    {
      field: 'total_clicked',
      headerName: 'Clicks',
      width: 100,
      sortable: true,
      type: 'number' as const,
    },
  ];

  if (loading && contacts.length === 0) {
    return (
      <Box display="flex" justifyContent="center" p={4}>
        <CircularProgress />
      </Box>
    );
  }

  if (!selectedClientId) {
    return (
      <Box textAlign="center" py={4}>
        <Typography variant="body2" color="text.secondary">
          Please select a client to view engagement data.
        </Typography>
      </Box>
    );
  }

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        {onSpamCheckerChange && (
          <Tooltip 
            title={
              <Box>
                <Typography variant="body2" color="white" sx={{ mb: 1 }}>
                  <strong>Filter out likely spam checker clicks</strong>
                </Typography>
                <Typography variant="body2" color="rgba(255, 255, 255, 0.8)">
                  When enabled, excludes opens and clicks from IP addresses flagged as spam checkers. 
                  This provides more accurate engagement metrics by filtering out automated bot activity.
                </Typography>
              </Box>
            }
            arrow
            placement="top"
            sx={{
              backgroundColor: 'rgba(0, 0, 0, 0.95)',
              color: 'white',
              border: '1px solid rgba(255, 255, 255, 0.1)',
            }}
          >
            <FormControlLabel
              control={
                <Switch
                  checked={includeSpamChecker}
                  onChange={(e) => onSpamCheckerChange(e.target.checked)}
                  color="primary"
                />
              }
              label="Filter out likely spam checker clicks"
              sx={{ 
                '& .MuiFormControlLabel-label': {
                  fontSize: '0.875rem',
                  color: 'text.secondary'
                }
              }}
            />
          </Tooltip>
        )}
        <DateRangePicker
          dateRange={dateRange}
          onDateRangeChange={onDateRangeChange}
        />
      </Box>

      {error ? (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      ) : (
        <Paper elevation={1} sx={{ height: 'calc(100vh - 400px)', width: '100%', display: 'flex', flexDirection: 'column' }}>
          <Box display="flex" justifyContent="space-between" alignItems="center" p={2} borderBottom="1px solid rgba(224, 224, 224, 1)">
            <Typography variant="h6">
              Contact Engagement Data
            </Typography>
            <Box display="flex" alignItems="center" gap={1}>
              <Tooltip title={
                contacts.length > 500 
                  ? "Map view is limited to 500 or fewer data points" 
                  : "View geographic distribution"
              }>
                <IconButton 
                  onClick={handleOpenMapModal}
                  disabled={contacts.length === 0 || contacts.length > 500}
                  size="small"
                >
                  <MapIcon />
                </IconButton>
              </Tooltip>
              {!isICRole() && (
                isExporting ? (
                  <Box display="flex" alignItems="center" gap={1}>
                    <CircularProgress size={16} />
                    <Typography variant="body2" color="text.secondary">
                      Exporting... ({exportProgress.current.toLocaleString()}/{exportProgress.total.toLocaleString()})
                    </Typography>
                  </Box>
                ) : (
                  <Tooltip title="Export contacts to CSV">
                    <IconButton
                      onClick={handleExport}
                      disabled={contacts.length === 0}
                      size="small"
                    >
                      <DownloadIcon />
                    </IconButton>
                  </Tooltip>
                )
              )}
            </Box>
          </Box>
          <Box sx={{ flex: 1, minHeight: 0 }}>
            <DataGrid
              rows={contacts}
              columns={columns}
              rowCount={totalCount}
              paginationMode="server"
              onPaginationModelChange={handlePaginationModelChange}
              paginationModel={paginationModel}
              sortingMode="server"
              onSortModelChange={handleSortModelChange}
              sortModel={sortModel}
              filterMode="server"
              onFilterModelChange={handleFilterModelChange}
              filterModel={filterModel}
              pageSizeOptions={[25, 50, 100]}
              disableRowSelectionOnClick
              loading={loading}
              getRowId={(row) => row.contact_id}
              autoHeight={false}
              sx={{
                height: '100%',
                '& .MuiDataGrid-cell': {
                  borderBottom: '1px solid rgba(224, 224, 224, 1)',
                },
                '& .MuiDataGrid-columnHeaders': {
                  backgroundColor: 'rgba(0, 0, 0, 0.04)',
                  borderBottom: '2px solid rgba(224, 224, 224, 1)',
                },
              }}
            />
          </Box>
        </Paper>
      )}

      {contacts.length === 0 && !loading && (
        <Box textAlign="center" py={4}>
          <Typography variant="body2" color="text.secondary">
            No {getMetricLabel().toLowerCase()} data found for the selected criteria.
          </Typography>
        </Box>
      )}

      {selectedContact && (
        <ContactDetailModal
          open={!!selectedContact}
          onClose={handleCloseContactModal}
          contactId={selectedContact.id}
          clientId={selectedClientId}
          dateRange={dateRange}
        />
      )}

      <GeographicMapModal
        key={`engagement-map-modal-${selectedClientId}`}
        open={mapModalOpen}
        onClose={handleCloseMapModal}
        title="Contact Engagement Geographic Distribution"
        subtitle={`${getMetricLabel().toLowerCase()} by location${includeSpamChecker ? ' (excluding likely spam checker clicks)' : ' (including likely spam checker clicks)'}`}
        clientId={selectedClientId}
        mapData={mapData}
        loading={mapLoading}
        error={mapError}
      />
    </Box>
  );
};

export default Engagement;
